// app/javascript/components/Location.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { t } from '../utils/i18n';

const Location = ({ onBack, api }) => {
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    district: '',
    region: '',
    station: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const data = await api.get('/locations');
      setLocations(data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const filteredAndSortedLocations = useMemo(() => {
    let filtered = locations.filter(location =>
      location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.district?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.region?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const seen = new Set();
    filtered = filtered.filter(loc => {
      const key = `${(loc.name || '').trim().toLowerCase()}-${(loc.region || '').trim().toLowerCase()}-${(loc.district || '').trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const [field, order] = sortBy.split('-');
    return filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      if (field === 'id') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [locations, searchTerm, sortBy]);

  const handleSortSelect = (sortOption) => {
    setSortBy(sortOption);
    setDropdownOpen(false);
  };

  const handleAdd = () => {
    setFormData({ name: '', code: '', district: '', region: '', station: '' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (location) => {
    setFormData({
      name: location.name || '',
      code: location.code || '',
      district: location.district || '',
      region: location.region || '',
      station: location.station || ''
    });
    setEditingId(location.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (confirm('Are you sure you want to delete this location?')) {
      setLoading(true);
      try {
        await api.delete(`/locations/${id}`);
        setLocations(locations.filter(loc => loc.id !== id));
      } catch (error) {
        console.error('Failed to delete location:', error);
        alert('Failed to delete location');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        // Edit existing
        const { response, data } = await api.put(`/locations/${editingId}`, { location: formData });
        if (response.ok) {
          setLocations(locations.map(loc =>
            loc.id === editingId ? data : loc
          ));
        } else {
          alert('Failed to update location');
        }
      } else {
        // Add new
        const { response, data } = await api.post('/locations', { location: formData });
        if (response.ok) {
          setLocations([...locations, data]);
        } else {
          alert('Failed to create location');
        }
      }
      setShowForm(false);
      setFormData({ name: '', code: '', district: '', region: '', station: '' });
      fetchLocations();
    } catch (error) {
      console.error('Failed to save location:', error);
      alert('Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ name: '', code: '', district: '', region: '', station: '' });
    setEditingId(null);
  };

  return (
    <>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb_40 baseTableWrap">
          <div className="dFlex baseTableHd mb_16">
            <h2 className="txt20 fwMedium colorBlack">
              {editingId ? 'Edit Location' : 'Add New Location'}
            </h2>
          </div>
          <form onSubmit={handleSubmit} style={{ padding: '24px 20px' }}>
            <div className="row">
              <div className="col-md-6 mb_20">
                <label className="mb_8">Location Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">Location Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">District</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.district}
                  onChange={(e) => setFormData({...formData, district: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">Region</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.region}
                  onChange={(e) => setFormData({...formData, region: e.target.value})}
                  required
                />
              </div>
              <div className="col-md-6 mb_20">
                <label className="mb_8">Station</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.station}
                  onChange={(e) => setFormData({...formData, station: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>
                {loading ? 'Saving...' : (editingId ? t('update') : t('add'))}
              </button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel}>
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Location List */}
      <div className="mb_40 baseTableWrap">
        <div className="dFlex baseTableHd mb_16">
          <h2 className="txt20 fwMedium colorBlack">Location List ({filteredAndSortedLocations.length})</h2>
          <div className="dFlex baseTableTR">
            <button className="btnAdd me-3" onClick={handleAdd}>
              Add Location
            </button>
            <div className="searchBox">
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="button">
                <img src="/images/search1.png" alt="" />
              </button>
            </div>
            <div className="dropdown dropdownSortBy">
              <button
                className="dropdown-toggle"
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {t('sort_by')}
              </button>
              <ul className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-asc')}>Name A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('name-desc')}>Name Z-A</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('code-asc')}>Code A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('region-asc')}>Region A-Z</a></li>
                <li><a className="dropdown-item" href="#" onClick={() => handleSortSelect('created-desc')}>Newest First</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="baseTableContainer">
          <table className="baseTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('name')}</th>
                <th>{t('code')}</th>
                <th>{t('district')}</th>
                <th>{t('region')}</th>
                <th>{t('station')}</th>
                <th>{t('created_date')}</th>
                <th style={{ minWidth: "175px" }}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLocations.length > 0 ? (
                filteredAndSortedLocations.map((location) => (
                  <tr key={location.id}>
                    <td>{location.id}</td>
                    <td>{location.name}</td>
                    <td>{location.code}</td>
                    <td>{location.district}</td>
                    <td>{location.region}</td>
                    <td>{location.station}</td>
                    <td>{location.created_at ? new Date(location.created_at).toLocaleDateString() : ''}</td>
                    <td>
                      <div className="dFlex optBlock">
                        <button className="optBtn" onClick={() => handleEdit(location)} disabled={loading}>
                          <img src="/images/edit.png" alt="" />
                          <span>{t('edit')}</span>
                        </button>
                        <span className="bar">|</span>
                        <button className="optBtn" onClick={() => handleDelete(location.id)} disabled={loading}>
                          <img src="/images/delete.png" alt="" />
                          <span>{t('delete')}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{textAlign: 'center', padding: '20px'}}>
                    No locations found matching "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Location;
